import type { BookType } from "@book-club/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import booksService from "@/features/books/services/booksService";

type ScheduleBookVariables = {
    author?: string;
    bookId: string | null;
    bookType: BookType;
    month: number;
    sessionDates: string[];
    themeTitle?: string;
    year: number;
};

/**
 * Mutation hook for scheduling a book
 * Invalidates books queries on success and navigates to books page
 */
export function useScheduleBook() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async ({
            bookId,
            bookType,
            year,
            month,
            sessionDates,
            author,
            amazonUrl,
            themeTitle,
        }: ScheduleBookVariables & { amazonUrl?: string }) => {
            return await booksService.scheduleBook(
                bookId,
                bookType,
                year,
                month,
                sessionDates,
                author,
                amazonUrl,
                themeTitle
            );
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : "Failed to schedule book";
            toast.error(errorMessage);
        },
        onSuccess: () => {
            // Invalidate all books queries
            queryClient.invalidateQueries({ queryKey: booksKeys.all });

            toast.success("Book scheduled successfully");
            navigate("/books");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
